import { Component } from '@theme/component';

/**
 * Horizontal media rail.
 *
 * The rail itself is a scroll container - the browser does the swiping, the
 * momentum, the scroll-snap and the trackpad gestures, and it all works with no
 * JavaScript at all. This adds the previous/next buttons and keeps their
 * disabled state honest, which is the part CSS cannot express.
 *
 * Direction-agnostic by construction: `scrollLeft` runs negative in RTL, so
 * every comparison here is against a normalised position rather than a raw
 * offset, and "next" means the next slide in reading order on both scripts.
 */
class AsxRail extends Component {
  requiredRefs = ['track'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const track = this.refs.track;
    if (!(track instanceof HTMLElement)) return;

    // The controls are useless without script, so they only appear with it.
    for (const control of this.querySelectorAll('[data-rail-control]')) control.hidden = false;

    track.addEventListener('scroll', this.#onScroll, {
      passive: true,
      signal: this.#abortController.signal,
    });

    if ('ResizeObserver' in window) {
      this.#resize = new ResizeObserver(() => this.#sync());
      this.#resize.observe(track);
    }

    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resize?.disconnect();
    this.#abortController.abort();
  }

  /** @type {ResizeObserver | undefined} */
  #resize;

  // Synced straight from the scroll event rather than deferred to a frame.
  // The work is two reads and two boolean writes, and scrolling does not
  // invalidate layout, so there is nothing here worth throttling - while
  // deferring it leaves the buttons showing the previous position's state
  // whenever frames are throttled, which is exactly when it is least welcome.
  #onScroll = () => this.#sync();

  /** Distance scrolled from the start edge, positive in both directions. */
  get #position() {
    const track = this.refs.track;
    return track instanceof HTMLElement ? Math.abs(track.scrollLeft) : 0;
  }

  get #maxPosition() {
    const track = this.refs.track;
    if (!(track instanceof HTMLElement)) return 0;
    return Math.max(0, track.scrollWidth - track.clientWidth);
  }

  #sync() {
    const position = this.#position;
    const max = this.#maxPosition;
    // A pixel of slack: sub-pixel layout means the end is rarely exact.
    const atStart = position <= 1;
    const atEnd = position >= max - 1;

    const previous = this.refs.previous;
    const next = this.refs.next;
    if (previous instanceof HTMLButtonElement) previous.disabled = atStart;
    if (next instanceof HTMLButtonElement) next.disabled = atEnd;

    this.toggleAttribute('data-scrollable', max > 1);
  }

  /** @param {number} direction */
  #step(direction) {
    const track = this.refs.track;
    if (!(track instanceof HTMLElement)) return;

    const first = track.querySelector('[data-rail-item]');
    const stride = first instanceof HTMLElement ? first.getBoundingClientRect().width + 16 : track.clientWidth * 0.8;

    // `scrollBy` takes a signed value in the writing direction, so the sign is
    // flipped for RTL rather than the intent being flipped.
    const rtl = getComputedStyle(this).direction === 'rtl';
    track.scrollBy({
      left: stride * direction * (rtl ? -1 : 1),
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  /** Bound declaratively from the markup. */
  previous = () => this.#step(-1);

  /** Bound declaratively from the markup. */
  next = () => this.#step(1);
}

if (!customElements.get('asx-rail')) {
  customElements.define('asx-rail', AsxRail);
}

export default AsxRail;
